import React, { useEffect, useState, useRef, useCallback } from "react";
import { CheckCircle2, XCircle, Search, X, Shield, GitBranch, Bug, FileSearch, Package, Clock } from "lucide-react";

interface ScanProgressModalProps {
  isOpen: boolean;
  onComplete: (findingsCount: number) => void;
  onClose: () => void;
  onFinished?: () => void;
  repoName: string;
  jobId?: string | null;
  startedAt?: number | null; // epoch ms from when scan was triggered
}

type ScanStatus = "pending" | "running" | "completed" | "failed";

interface JobStatusResponse {
  job_id: string;
  status: ScanStatus;
  phase?: string;
  message?: string;
  findings_count?: number;
  error?: string;
  /** 1-based place in the queue while this scan is still waiting its turn. */
  queue_position?: number | null;
}

const SCAN_PHASES = [
  { key: "queue",      label: "Queuing scan job",       icon: Clock,      estSeconds: 5  },
  { key: "cloning",    label: "Cloning repository",     icon: GitBranch,  estSeconds: 10 },
  { key: "sast",       label: "Static analysis (SAST)", icon: FileSearch, estSeconds: 60 },
  { key: "sca",        label: "Dependency scanning",    icon: Package,    estSeconds: 30 },
  { key: "secrets",    label: "Secrets detection",       icon: Shield,     estSeconds: 20 },
  { key: "iac",        label: "IaC & license scanning", icon: Bug,        estSeconds: 30 },
  { key: "processing", label: "Processing results",     icon: FileSearch, estSeconds: 10 },
];

// Map the worker's real phase strings (reported via /v1/jobs/:id/phase:
// cloning, downloading, extracting, scanning, processing, code_health) onto a
// SCAN_PHASES step index. The scanners run as one "scanning" phase on the
// backend, so it lands on the first scanner step and the rest read as pending.
const PHASE_TO_STEP: Record<string, number> = {
  queue: 0, queued: 0, pending: 0,
  cloning: 1, downloading: 1, extracting: 1,
  scanning: 2, sast: 2, sca: 3, secrets: 4, iac: 5, code_health: 5,
  processing: 6,
};

// Determine which phase we should be in based on elapsed seconds. Used ONLY as
// a fallback before the backend reports its first real phase — the scan time
// varies enormously by repo size, so time is not a reliable driver.
function getPhaseByTime(elapsedSec: number): number {
  let accum = 0;
  for (let i = 0; i < SCAN_PHASES.length; i++) {
    accum += SCAN_PHASES[i].estSeconds;
    if (elapsedSec < accum) return i;
  }
  return SCAN_PHASES.length - 1;
}

// Honest, phase-driven progress. Each phase advances toward a ceiling and the
// long "scanning" phase crawls slowly so the bar keeps moving on a big repo
// without ever reaching 100% until the scan actually completes. Previously
// progress was a pure function of a fixed 165s estimate, so it hit 95% in under
// three minutes and then sat frozen for the rest of a multi-minute scan.
function progressForPhase(phase: string | null, elapsedSec: number): number {
  switch (phase) {
    case "cloning": case "downloading": case "extracting":
      return Math.min(25, 12 + elapsedSec * 0.2);
    case "scanning": case "sast": case "sca": case "secrets": case "iac": case "code_health":
      return Math.min(85, 28 + elapsedSec * 0.12);
    case "processing":
      return 92;
    default: // queue / pending / not-yet-reported
      return Math.min(10, 3 + elapsedSec * 0.5);
  }
}

function formatTime(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return min > 0 ? `${min}m ${s}s` : `${sec}s`;
}

export default function ScanProgressModal({
  isOpen,
  onComplete,
  onClose,
  onFinished,
  repoName,
  jobId,
  startedAt,
}: ScanProgressModalProps) {
  const [status, setStatus] = useState<ScanStatus>("pending");
  const [findingsCount, setFindingsCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  // Real phase + message reported by the worker, so the UI reflects what is
  // actually happening rather than a fixed time estimate.
  const [phase, setPhase] = useState<string | null>(null);
  const [phaseMessage, setPhaseMessage] = useState<string | null>(null);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const tickRef = useRef<NodeJS.Timeout | null>(null);
  const doneRef = useRef(false);

  // Elapsed time derived from startedAt — consistent across reopen
  const elapsed = startedAt ? Math.max(0, now - startedAt) : 0;
  const elapsedSec = elapsed / 1000;
  const isComplete = status === "completed";
  const isFailed = status === "failed";
  const isRunning = !isComplete && !isFailed;

  // Prefer the worker's real phase; fall back to a time estimate only in the
  // brief window before the first phase update arrives.
  const backendStep = phase != null ? (PHASE_TO_STEP[phase] ?? 2) : null;
  const currentPhase = isComplete
    ? SCAN_PHASES.length
    : backendStep != null
    ? backendStep
    : Math.min(1, getPhaseByTime(elapsedSec)); // don't run ahead before we have real data
  const progress = isComplete ? 100 : progressForPhase(phase, elapsedSec);

  // Tick timer — updates `now` every second for smooth elapsed display
  useEffect(() => {
    if (!isOpen || !startedAt) return;
    doneRef.current = false;
    setStatus("running");
    setFindingsCount(null);
    setError(null);
    setPhase(null);
    setPhaseMessage(null);
    setNow(Date.now());

    tickRef.current = setInterval(() => {
      if (!doneRef.current) setNow(Date.now());
    }, 1000);

    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [isOpen, startedAt]);

  // Poll job status
  const poll = useCallback(async () => {
    if (!jobId || doneRef.current) return;
    try {
      const { apiFetch } = await import("../api/client");
      const data = await apiFetch<JobStatusResponse>({ path: `/v1/jobs/${jobId}/status` });

      setStatus(data.status);
      if (data.phase) setPhase(data.phase);
      if (data.message) setPhaseMessage(data.message);
      setQueuePosition(
        typeof data.queue_position === "number" ? data.queue_position : null
      );

      if (data.status === "completed") {
        doneRef.current = true;
        setFindingsCount(data.findings_count ?? 0);
        if (pollRef.current) clearInterval(pollRef.current);
        if (tickRef.current) clearInterval(tickRef.current);
        onFinished?.();
      } else if (data.status === "failed") {
        doneRef.current = true;
        setError(data.error || "Scan failed");
        if (pollRef.current) clearInterval(pollRef.current);
        if (tickRef.current) clearInterval(tickRef.current);
        onFinished?.();
      }
    } catch {
      // continue polling
    }
  }, [jobId, onFinished]);

  useEffect(() => {
    if (!isOpen || !jobId) return;
    poll();
    pollRef.current = setInterval(poll, 2000);

    const timeout = setTimeout(() => {
      if (!doneRef.current) {
        doneRef.current = true;
        setStatus("failed");
        setError("Scan timed out after 30 minutes");
        if (pollRef.current) clearInterval(pollRef.current);
        if (tickRef.current) clearInterval(tickRef.current);
        onFinished?.();
      }
    }, 30 * 60 * 1000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      clearTimeout(timeout);
    };
  }, [isOpen, jobId, poll, onFinished]);

  if (!isOpen) return null;

  const phaseLabel = isComplete
    ? "Scan complete!"
    : isFailed
    ? (error || "Scan failed")
    : // Scans run one at a time, so say where in the queue this one is —
      // otherwise waiting a turn is indistinguishable from being stuck.
      queuePosition && queuePosition > 1
    ? `Waiting in queue — ${queuePosition} scans ahead of this one...`
    : queuePosition === 1
    ? "Next in queue — starting shortly..."
    : phaseMessage // the worker's real message, e.g. "Processing 282 findings..."
    ? phaseMessage
    : currentPhase < SCAN_PHASES.length
    ? SCAN_PHASES[currentPhase].label + "..."
    : "Finishing up...";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={isRunning ? undefined : onClose} />

      <div className="relative w-full max-w-lg mx-4 animate-in fade-in zoom-in-95 duration-300">
        <div className="rounded-xl border border-border bg-bg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative px-6 py-5 border-b border-border bg-surface1 overflow-hidden">
            <div className={`absolute inset-0 ${
              isComplete ? "bg-success/5" :
              isFailed ? "bg-critical/5" :
              "bg-brand/5"
            }`} />
            <button onClick={onClose} className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-muted hover:text-text hover:bg-surface2 transition-colors" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
            <div className="relative flex items-center justify-between pr-8">
              <div>
                <h2 className="text-lg font-semibold text-text flex items-center gap-2">
                  {isComplete ? (
                    <><CheckCircle2 className="h-5 w-5 text-success" />Scan Complete</>
                  ) : isFailed ? (
                    <><XCircle className="h-5 w-5 text-critical" />Scan Failed</>
                  ) : (
                    <><Search className="h-5 w-5 text-brand animate-pulse" />Scanning Repository</>
                  )}
                </h2>
                <p className="text-sm text-muted mt-0.5 font-mono truncate max-w-[240px]">{repoName}</p>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold font-mono tabular-nums ${
                  isComplete ? "text-success" : isFailed ? "text-critical" : "text-brand"
                }`}>
                  {formatTime(elapsed)}
                </div>
                <div className="text-xs text-muted">elapsed</div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 bg-bg">
            {/* Progress bar */}
            {isRunning && (
              <div className="mb-5">
                <div className="flex justify-between text-xs text-muted mb-1.5">
                  <span>{phaseLabel}</span>
                  <span className="font-mono">{Math.round(progress)}%</span>
                </div>
                <div className="h-2.5 bg-surface2 rounded-full overflow-hidden border border-border/30">
                  <div
                    className="h-full bg-gradient-to-r from-brand to-focus rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Phase steps */}
            <div className="space-y-1">
              {SCAN_PHASES.map((p, i) => {
                const Icon = p.icon;
                const isDone = i < currentPhase || isComplete;
                const isCurrent = i === currentPhase && isRunning;

                return (
                  <div key={p.key} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                    isCurrent ? "bg-brand/10 border border-brand/20" :
                    isDone ? "" : ""
                  }`}>
                    <div className={`flex-shrink-0 w-5 h-5 flex items-center justify-center ${
                      isDone ? "text-success" : isCurrent ? "text-brand" : "text-muted"
                    }`}>
                      {isDone ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : isCurrent ? (
                        <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    <span className={`text-sm ${
                      isCurrent ? "text-text font-medium" :
                      isDone ? "text-muted line-through" : "text-muted"
                    }`}>
                      {p.label}
                    </span>
                    {isDone && <CheckCircle2 className="h-3 w-3 text-success ml-auto" />}
                    {/* No per-step time estimate: step duration varies by orders
                        of magnitude with repo size, and showing "~5s" beside a
                        step that has been running for minutes is the same false
                        precision as the old overall countdown. */}
                  </div>
                );
              })}
            </div>

            {/* Findings count */}
            {isComplete && findingsCount !== null && (
              <div className="bg-surface1 rounded-xl p-4 mt-4 border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-muted">Findings detected:</span>
                  <span className={`text-2xl font-bold ${findingsCount > 0 ? "text-medium" : "text-success"}`}>
                    {findingsCount}
                  </span>
                </div>
              </div>
            )}

            {/* Error */}
            {isFailed && error && (
              <div className="bg-critical/10 border border-critical/20 rounded-lg px-4 py-3 mt-4">
                <p className="text-critical text-sm">{error}</p>
              </div>
            )}

            {/* Background note */}
            {isRunning && (
              <p className="text-xs text-muted text-center mt-4">
                Large repositories can take several minutes. The scan keeps running in the background if you close this dialog.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-surface1 flex justify-end gap-3">
            {isRunning && (
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-surface1 text-text hover:bg-surface2 transition-colors">
                Continue Browsing
              </button>
            )}
            {isComplete && (
              <button onClick={() => { onComplete(findingsCount ?? 0); onClose(); }} className="px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white hover:bg-brand-hover transition-colors">
                View Results →
              </button>
            )}
            {isFailed && (
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-surface1 text-text hover:bg-surface2 transition-colors">
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
